/**
 * LivingEventsModule — the client half of the living-events subsystem for
 * everything that is NOT a battle (BattleModule owns those).
 *
 * Reads `living_event` rows (a small, retention-bounded table) and, for each
 * LIVE event within STAGE_RANGE_M, builds the stage of its family at the
 * venue, animates it every frame, and tears it down when the row ends or
 * the player walks away. It also:
 *   - fills EnvironmentModule's `facing` map so the parked crowd looks at the
 *     lectern / altar / fire instead of the player;
 *   - toasts the player when a new event opens within earshot;
 *   - keeps a tiny HUD chip listing the live events nearby with countdowns;
 *   - exposes `window.__livingEvents` for probes.
 *
 * Family → stage:  festival→FestivalStage  civic→CivicStage  faith→FaithStage
 *                  street→StreetScene      battle→(BattleModule)
 * To add a family: write a stage under ./stages and add one case to `build`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { makeSystem, Phase } from '../../engine-compat.js';
import { EVENT_KINDS } from '@shared/living_events.js';
import type { EnvironmentModule } from '../EnvironmentModule.js';
import type { Stage, StageCtx } from './stages/StageBase.js';
import { FestivalStage } from './stages/FestivalStage.js';
import { CivicStage } from './stages/CivicStage.js';
import { FaithStage } from './stages/FaithStage.js';
import { StreetScene } from './stages/StreetScene.js';
import { prefetchCeremonyProps } from './ceremony_props_loader.js';

export interface LivingEventRow {
  eventId: bigint; worldId: bigint; settlementId: bigint; kind: string; family: string; x: number; z: number; hostId: bigint;
  title: string; detail: string; participantsJson: string; startsAt: { microsSinceUnixEpoch: bigint }; endsAt: { microsSinceUnixEpoch: bigint };
  phase: string; refId: bigint;
}

/** Stages are built only this close (a festival 2 km away is a toast, not a draw). */
export const STAGE_RANGE_M = 900;
export const TOAST_RANGE_M = 2500;

interface Entry { stage: Stage; row: LivingEventRow; facingIds: string[] }

export class LivingEventsModule implements IModule {
  readonly name = 'LivingEvents';
  private readonly root = new THREE.Group();
  private readonly stages = new Map<string, Entry>();
  private hud: HTMLDivElement | null = null;
  private hudAt = 0;
  private subscribed = false;
  private subDiag = { applied: false, error: '', appliedAt: 0 };
  private disposed = false;

  constructor(private readonly env: EnvironmentModule) {}

  init(app: App): void {
    this.root.name = 'living-events';
    app.world.scene.add(this.root);
    app.world.addSystem(makeSystem('LivingEventsRender', Phase.Animation, 40, (dt) => this.frame(dt)));
    // The generated ceremony props (Hugging Face wave) stream in the background; a stage built
    // before they land keeps its procedural furniture.
    void prefetchCeremonyProps(app);
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    const hud = document.createElement('div');
    hud.id = 'living-events-hud';
    hud.style.cssText = 'position:fixed;top:64px;right:14px;z-index:40;display:none;flex-direction:column;gap:4px;pointer-events:none;font:12px/1.3 system-ui;color:#f3ecff;';
    document.body.appendChild(hud);
    this.hud = hud;
    (window as unknown as Record<string, unknown>).__livingEvents = {
      live: () => this.liveRows().map((e) => ({
        id: e.eventId.toString(), kind: e.kind, family: e.family, title: e.title, x: e.x, z: e.z, hostId: e.hostId.toString(),
        participants: participantsOf(e).length, endsInS: Math.round(remainingS(e)), dist: Math.round(Math.hypot(e.x - atlas.localX, e.z - atlas.localZ)),
      })),
      stages: () => [...this.stages.entries()].map(([id, s]) => ({ id, kind: s.row.kind, ...s.stage.describe() })),
      facing: () => this.env.facing.size,
      hud: () => this.hud?.textContent ?? '',
      diag: () => ({ subscribed: this.subscribed, ...this.subDiag, rows: this.liveRows().length, at: [Math.round(atlas.localX), Math.round(atlas.localZ)], active: (atlas.conn as unknown as { isActive?: boolean } | null)?.isActive ?? null }),
    };
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subscribed) return;
    this.subscribed = true;
    try {
      conn.subscriptionBuilder()
        .onApplied(() => { this.subDiag.applied = true; this.subDiag.appliedAt = Date.now(); })
        .onError((ctx) => { const ev = (ctx as { event?: unknown }).event; this.subDiag.error = String((ev as Error)?.message ?? ev ?? 'subscription error'); })
        .subscribe(['SELECT * FROM living_event']);
      conn.db.livingEvent.onInsert((_c, row) => this.announce(row as LivingEventRow));
    } catch { /* pre-publish server */ }
  }

  private announce(e: LivingEventRow): void {
    if (e.phase !== 'live' || e.family === 'battle') return;
    const def = EVENT_KINDS[e.kind];
    if (!def) return;
    const d = Math.hypot(e.x - atlas.localX, e.z - atlas.localZ);
    if (d > TOAST_RANGE_M) return;
    const mins = Math.max(1, Math.round(remainingS(e) / 60));
    bus.emit('atlas:status', { text: `${def.glyph} ${def.label}: ${e.title} — ${Math.round(d)} m away, ${mins} min` });
  }

  private liveRows(): LivingEventRow[] {
    const conn = atlas.conn;
    const out: LivingEventRow[] = [];
    if (!conn) return out;
    const nowUs = BigInt(Date.now()) * 1000n;
    try {
      for (const e of conn.db.livingEvent.iter()) {
        if (e.worldId !== atlas.currentWorldId) continue; // (bug sweep) cross-world festivals staged into the wrong world
        if (e.phase === 'live' && e.endsAt.microsSinceUnixEpoch > nowUs) out.push(e as LivingEventRow);
      }
    } catch { /* pre-publish server */ }
    return out;
  }

  private frame(dt: number): void {
    if (this.disposed) return;
    const t = performance.now() / 1000;
    const seen = new Set<string>();
    const rows = this.liveRows();
    for (const e of rows) {
      if (e.family === 'battle') continue;
      if (Math.hypot(e.x - atlas.localX, e.z - atlas.localZ) > STAGE_RANGE_M) continue;
      const key = e.eventId.toString();
      seen.add(key);
      let entry = this.stages.get(key);
      if (!entry) { const built = this.build(e); if (!built) continue; entry = built; this.stages.set(key, entry); }
      entry.row = e;
      entry.stage.update(t, dt);
    }
    for (const [key, entry] of this.stages) {
      if (seen.has(key)) continue;
      entry.stage.dispose();
      for (const id of entry.facingIds) this.env.facing.delete(id);
      this.stages.delete(key);
    }
    if (t - this.hudAt > 0.5) { this.hudAt = t; this.renderHud(rows); }
  }

  private build(e: LivingEventRow): Entry | null {
    const def = EVENT_KINDS[e.kind];
    if (!def) return null;
    const entry: Partial<Entry> = { row: e, facingIds: [] };
    const ctx: StageCtx = {
      root: this.root,
      groundAt: (x, z) => this.env.groundAt(x, z),
      x: e.x, z: e.z, y: this.env.groundAt(e.x, e.z),
      title: e.title, detail: e.detail, def, hostId: e.hostId.toString(), participantIds: participantsOf(e),
      versonaPosition: (id) => this.env.versonaPosition(id),
      remainingS: () => remainingS(entry.row ?? e),
    };
    let stage: Stage;
    switch (def.family) {
      case 'festival': stage = new FestivalStage(ctx); break;
      case 'civic': stage = new CivicStage(ctx); break;
      case 'faith': stage = new FaithStage(ctx); break;
      case 'street': stage = new StreetScene(ctx); break;
      default: return null;
    }
    // Where the crowd looks: the lectern / altar (host slot) for civic + faith
    // events and the fire for festivals; the host looks back at the crowd.
    const ids = ctx.participantIds;
    if (def.family === 'civic' || def.family === 'faith') {
      for (const id of ids) this.env.facing.set(id, id === ctx.hostId ? { x: e.x - 3, z: e.z } : { x: e.x + 1.2, z: e.z });
      entry.facingIds = ids.slice();
    } else if (def.family === 'festival') {
      for (const id of ids) this.env.facing.set(id, { x: e.x, z: e.z });
      entry.facingIds = ids.slice();
    }
    entry.stage = stage;
    return entry as Entry;
  }

  private renderHud(rows: LivingEventRow[]): void {
    const hud = this.hud;
    if (!hud) return;
    const near = rows
      .map((e) => ({ e, d: Math.hypot(e.x - atlas.localX, e.z - atlas.localZ) }))
      .filter((x) => x.d <= TOAST_RANGE_M && x.e.family !== 'street')
      .sort((a, b) => a.d - b.d)
      .slice(0, 4);
    if (near.length === 0) { hud.style.display = 'none'; hud.textContent = ''; return; }
    hud.style.display = 'flex';
    hud.replaceChildren(...near.map(({ e, d }) => {
      const def = EVENT_KINDS[e.kind];
      const left = Math.max(0, remainingS(e));
      const mm = Math.floor(left / 60), ss = Math.floor(left % 60);
      const row = document.createElement('div');
      row.className = 'living-event-chip';
      row.style.cssText = 'background:rgba(18,12,30,0.72);border:1px solid rgba(255,255,255,0.18);border-radius:8px;padding:4px 9px;backdrop-filter:blur(4px);';
      row.textContent = `${def?.glyph ?? '•'} ${def?.label ?? e.kind}: ${e.title} · ${Math.round(d)} m · ${mm}:${String(ss).padStart(2, '0')}`;
      return row;
    }));
  }

  dispose(): void {
    this.disposed = true;
    for (const [, entry] of this.stages) { entry.stage.dispose(); for (const id of entry.facingIds) this.env.facing.delete(id); }
    this.stages.clear();
    this.hud?.remove();
    this.root.parent?.remove(this.root);
  }
}

export function participantsOf(e: LivingEventRow): string[] {
  try { return (JSON.parse(e.participantsJson) as string[]).map(String); } catch { return []; }
}

export function remainingS(e: LivingEventRow): number {
  return Number(e.endsAt.microsSinceUnixEpoch - BigInt(Date.now()) * 1000n) / 1e6;
}
